import { forwardRef, type ButtonHTMLAttributes, type Ref, type ReactNode } from 'react';
import './Burger.css';

export interface BurgerProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  /** Show as the X-state (open) or the bar-state (closed). */
  opened?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  color?: string;
  /** Visible-label-equivalent for screen readers (default: "Toggle navigation"). */
  label?: string;
}

const SIZE_PX = { xs: 18, sm: 22, md: 28, lg: 36, xl: 44 } as const;
function dim(s: BurgerProps['size']): number {
  if (s === undefined) return SIZE_PX.md;
  if (typeof s === 'number') return s;
  return SIZE_PX[s as keyof typeof SIZE_PX] ?? SIZE_PX.md;
}

/** Animated hamburger button. Toggles between three bars and an X. */
export const Burger = forwardRef(function Burger(
  { opened, size = 'md', color, label = 'Toggle navigation', className, ...rest }: BurgerProps,
  ref: Ref<HTMLButtonElement>
): ReactNode {
  const px = dim(size);
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      aria-expanded={opened}
      data-opened={opened || undefined}
      className={['rr-burger', className].filter(Boolean).join(' ')}
      style={{ width: px, height: px, color }}
      {...rest}
    >
      <span className="rr-burger__bar" />
      <span className="rr-burger__bar" />
      <span className="rr-burger__bar" />
    </button>
  );
});
